import { z } from 'zod';
import { ActorSchema } from '../common/actor.schema';
import { DocumentAnalysisStatusSchema } from '../property/document-analysis.schemas';
import { DocumentCategorySchema } from '../property/documents.schemas';

export const PropertyDocumentClassificationOutcomeSchema = z.enum([
  'CATEGORIZED',
  'UNCATEGORIZED',
  'PROMOTED_TO_GALLERY',
  'FAILED',
]);

export type PropertyDocumentClassificationOutcome = z.infer<typeof PropertyDocumentClassificationOutcomeSchema>;

/**
 * Emitted once per document when the classification poller reaches a terminal
 * outcome. The list refetches the row for its category, so the payload carries
 * ids and the outcome, not the classification result.
 */
export const PropertyDocumentClassifiedPayloadSchema = z.object({
  propertyDocumentId: z.uuid(),
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  outcome: PropertyDocumentClassificationOutcomeSchema,
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PropertyDocumentClassifiedPayload = z.infer<typeof PropertyDocumentClassifiedPayloadSchema>;

/**
 * Emitted at every state the analysis poller drives one document's analysis
 * through — the claim, the IMM-469.3 defer back to PENDING, and the three
 * terminal outcomes — so an open Dokumente tab can move that row in place.
 *
 * Deliberately a separate leaf from `property.document-analysis-completed`,
 * whose facts subscriber must only ever see a COMPLETED run: the tab needs all
 * of them, the facts derivation exactly one, and a shared event would put a
 * filter between the publish and the facts layer whose omission silently
 * corrupts a property's facts.
 *
 * `status` is the state AFTER the transition, so a consumer never has to know
 * which call site published it. `organizationId` is not on the analysis row and
 * is resolved at publish time — the stream's organization channel routes on it.
 */
export const PropertyDocumentAnalysisStateChangedPayloadSchema = z.object({
  analysisId: z.uuid(),
  propertyDocumentId: z.uuid(),
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  status: DocumentAnalysisStatusSchema,
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PropertyDocumentAnalysisStateChangedPayload = z.infer<
  typeof PropertyDocumentAnalysisStateChangedPayloadSchema
>;

/**
 * A member deleted a stored document from a property (IMM-1095). The write's
 * audit record: `facts-changed` alone is a derived signal and fires only when
 * the document had live facts.
 */
export const PropertyDocumentDeletedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  documentId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PropertyDocumentDeletedPayload = z.infer<typeof PropertyDocumentDeletedPayloadSchema>;

export const DocumentRedactionCheckDuePayloadSchema = z.object({
  redactionId: z.uuid(),
  attempt: z.number().int().min(1),
});

export type DocumentRedactionCheckDuePayload = z.infer<typeof DocumentRedactionCheckDuePayloadSchema>;

/**
 * A stored document was renamed or recategorised (IMM-1156.3): the write's audit record, published
 * for every actor. Ids, the change and the new category only — never the display name, which is
 * free text a member typed.
 */
export const PropertyDocumentRefiledPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  documentId: z.uuid(),
  change: z.enum(['RENAMED', 'RECATEGORIZED']),
  category: DocumentCategorySchema.nullable(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PropertyDocumentRefiledPayload = z.infer<typeof PropertyDocumentRefiledPayloadSchema>;

/**
 * A document's „Für Käufer“ mark was set or cleared (IMM-1176.1): by a member, or by the classifier
 * filing a marked document under Person & Legitimation (system actor). Ids and the new value only.
 */
export const PropertyDocumentBuyerSharingChangedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  documentId: z.uuid(),
  forBuyers: z.boolean(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PropertyDocumentBuyerSharingChangedPayload = z.infer<
  typeof PropertyDocumentBuyerSharingChangedPayloadSchema
>;
